import { memo, useState, useCallback, useMemo } from "react";
import {
  Grid,
  TextField,
  Chip,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@material-ui/core";
import { sucursalesApi } from "src/services/sucursalesApi";

const SucursalFilter = memo(
  ({
    id = "sucursal-filter",
    label = "Filtrar por sucursales",
    rutEmpleado,
    sucursalesSeleccionadas = [],
    onChangeSucursales,
    tamanio = 12,
    required = false,
    placeholder = "Selecciona una o más sucursales",
    variant = "outlined",
    size = "small",
    ...other
  }) => {
    const [inputValue, setInputValue] = useState("");

    const {
      data: sucursalesEmpleado = [],
      isFetching: cargandoSucursales,
      error: errorSucursales,
    } = sucursalesApi.useGetSucursalesEmpleadosQuery(rutEmpleado, {
      skip: !rutEmpleado,
    });

    const opcionesSucursales = sucursalesEmpleado.map((sucursal) => ({
      id: sucursal.refSucursal,
      nombre: sucursal.refSucursal,
      label: sucursal.label || sucursal.refSucursal,
      value: sucursal.refSucursal,
    }));

    const opcionTodas = useMemo(
      () => ({
        id: "TODAS",
        nombre: "Todas las sucursales",
        label: "Todas las sucursales",
        value: "TODAS",
      }),
      [],
    );

    const todasLasOpciones = [opcionTodas, ...opcionesSucursales];

    const handleInputChange = useCallback((event, newInputValue) => {
      setInputValue(newInputValue);
    }, []);

    // ✨ HANDLER MEJORADO CON EXCLUSIÓN MUTUA
    const handleChange = useCallback(
      (event, newValue) => {
        // Verificar si la última selección es "Todas"
        const ultimaSeleccion = newValue[newValue.length - 1];

        if (ultimaSeleccion?.value === "TODAS") {
          // Si selecciona "Todas", usar array vacío
          onChangeSucursales([]);
        } else {
          // Si selecciona sucursales específicas, excluir "TODAS" automáticamente
          const sucursalesEspecificas = newValue
            .filter((item) => item.value !== "TODAS")
            .map((sucursal) => sucursal.value);

          onChangeSucursales(sucursalesEspecificas);
        }
      },
      [onChangeSucursales],
    );

    // Determinar qué mostrar en el campo
    const valorMostrado = useMemo(() => {
      if (sucursalesSeleccionadas.length === 0) {
        return [opcionTodas];
      }

      return opcionesSucursales.filter((opcion) =>
        sucursalesSeleccionadas.includes(opcion.value),
      );
    }, [sucursalesSeleccionadas, opcionTodas, opcionesSucursales]);

    return (
      <Grid item xs={tamanio} {...other}>
        <Box>
          {label && (
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {label}
            </Typography>
          )}

          <Autocomplete
            multiple
            id={id}
            options={todasLasOpciones}
            value={valorMostrado}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            loading={cargandoSucursales}
            disabled={cargandoSucursales || !!errorSucursales}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  key={option.id}
                  label={option.label}
                  {...getTagProps({ index })}
                  size="small"
                  color={option.value === "TODAS" ? "secondary" : "primary"}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sucursales"
                variant={variant}
                size={size}
                required={required}
                error={!!errorSucursales}
                helperText={
                  errorSucursales
                    ? "Error al cargar"
                    : sucursalesSeleccionadas.length === 0
                      ? `Todas las sucursales (${opcionesSucursales.length})`
                      : `${sucursalesSeleccionadas.length} seleccionada${
                          sucursalesSeleccionadas.length > 1 ? "s" : ""
                        }`
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {cargandoSucursales ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body2">
                  {option.label}
                  {option.value === "TODAS" && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      component="span"
                    >
                      {` (${opcionesSucursales.length} sucursales)`}
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}
            noOptionsText={
              cargandoSucursales
                ? "Cargando sucursales..."
                : errorSucursales
                  ? "Error al cargar sucursales"
                  : "No hay sucursales disponibles"
            }
            filterSelectedOptions
            disableCloseOnSelect
          />
        </Box>
      </Grid>
    );
  },
);

export default SucursalFilter;
